"use client";

import React, { useState, useEffect } from 'react';
import { Block } from '../../interfaces.ts';
import TemplateSettingsDialog from './TemplateSettingsDialog.tsx';
import LocaleFieldWrapper from '../../components/LocaleFieldWrapper.tsx';
import {useLocale} from '../../context/localeContext.tsx';

interface HeaderBlockTemplateSettingsDialogProps {
  block: Block;
  onClose: () => void;
  onSave: (updatedBlock: Block) => void;
  availableLocales: string[];
  fallbackLocale?: string;
}

const HeaderBlockTemplateSettingsDialog: React.FC<HeaderBlockTemplateSettingsDialogProps> = ({
  block,
  onClose,
  onSave,
  availableLocales,
}) => {
  const { activeLocale } = useLocale();
  
  // Local state for content and properties (not part of context)
  const [localeContent, setLocaleContent] = useState<{
    [key: string]: { headerText: string };
  }>(
    availableLocales.reduce((acc: any, locale: string) => {
      acc[locale] = {
        headerText: block.defaultContent?.[locale]?.headerText || '',
      };
      return acc;
    }, {})
  );

  const [headerLevel, setHeaderLevel] = useState(block.properties?.headerLevel || 1);

  useEffect(() => {
    const currentContent = localeContent[activeLocale];
    if (!currentContent) {
      setLocaleContent((prev) => ({
        ...prev,
        [activeLocale]: { headerText: '' },
      }));
    }
  }, [activeLocale, localeContent]);

  const handleSave = () => {
    onSave({
      ...block,
      properties: {
        ...block.properties,
        headerLevel,
      },
      defaultContent: localeContent,
    });
  };

  const handleHeaderTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setLocaleContent((prev) => ({
      ...prev,
      [activeLocale]: { ...prev[activeLocale], headerText: e.target.value },
    }));
  };

  return (
    <TemplateSettingsDialog
      title="HeaderBlock Settings"
      onClose={onClose}
      onSave={handleSave}
    >
      <LocaleFieldWrapper>
        <label className="block mb-2">
          Default Header Text ({activeLocale.toUpperCase()}):
          <input
            type="text"
            className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
            value={localeContent[activeLocale]?.headerText || ''}
            onChange={handleHeaderTextChange}
          />
        </label>
      </LocaleFieldWrapper>
      <label className="block mb-2">
        Header Level:
        <select
          className="w-full border border-solid border-gray-300 rounded mt-1 p-1"
          value={headerLevel}
          onChange={(e) => setHeaderLevel(parseInt(e.target.value))}
        >
          {[1, 2, 3, 4, 5, 6].map((level) => (
            <option key={level} value={level}>
              H{level}
            </option>
          ))}
        </select>
      </label>
    </TemplateSettingsDialog>
  );
};

export default HeaderBlockTemplateSettingsDialog;
